import { Component, QueryList, ViewChild, ViewChildren, computed, signal } from '@angular/core';
import System from 'framework/System';
import BaseComponent from '../BaseComponent';
import Check from 'framework/Check';
import IValidatedResult = app.interfaces.IValidatedResult;
import { SaveTypes } from 'app/models/enums/SaveTypes';
import { PublishOptionsComponent } from '../../shared/publish-options/publish-options.component';
import Utils from '../../../shared/utils';
import { PreAuditDetailService } from '../../../services/pre-audit-detail.service';
import { WebsiteService } from '../../../services/website.service';
import { TabPanelDisplayMode } from 'app/components/enums/TabPanelDisplayMode';
import { CustomerAccountGroupComponent } from '../../../shared/components/customer-account-group/customer-account-group.component';
import ImplementationOwners = Boo.Objects.Enums.ImplementationOwners;
import CustomerAccountTypes = Boo.Objects.Enums.CustomerAccountTypes;

@Component({
    selector: 'app-components-ticket-logins-and-publish-options',
    templateUrl: './logins-and-publish-options.component.html'
})
export class LoginsAndPublishOptionsComponent extends BaseComponent {
    customer: CustomerObservable;
    isOpen = false;
    updatedContentManagementSystem: Boo.Objects.ContentManagementSystem;
    @ViewChild(PublishOptionsComponent) publishOptionsComponent: PublishOptionsComponent;
    @ViewChildren(CustomerAccountGroupComponent) customerAccountGroups: QueryList<CustomerAccountGroupComponent>;
    @ViewChild('cmsGroup') cmsGroup: CustomerAccountGroupComponent;
    currentImplementationOwner = signal(ImplementationOwners.NotSelected);
    isBoostabilityMakingChangesToTheSite = computed(() => this.currentImplementationOwner() === ImplementationOwners.Boostability);
    tabPanelDisplayMode = TabPanelDisplayMode;
    hasBlog: boolean;
    customerHasWebsite = false;
    cmsAccountTypes = [
        Boo.Objects.Enums.CustomerAccountTypes.CMSAdminAccess,
        Boo.Objects.Enums.CustomerAccountTypes.FTP
    ];
    websiteAccountTypes = [
        Boo.Objects.Enums.CustomerAccountTypes.DomainRegistrar,
        Boo.Objects.Enums.CustomerAccountTypes.WebsiteHosting
    ];
    blogAccountTypes = [
        Boo.Objects.Enums.CustomerAccountTypes.BlogAdmin,
        Boo.Objects.Enums.CustomerAccountTypes.BlogEditor,
        Boo.Objects.Enums.CustomerAccountTypes.BlogHosting
    ];

    constructor(
        private preAuditDetailService: PreAuditDetailService,
        private websiteService: WebsiteService) { super(); }

    activate(params: app.ticket.components.interfaces.ITicketComponentActivateParams): JQueryPromise<void> {
        this.isLoading(true);
        return super.activate(params)
            .then(() => {
                Check.isNotEmpty(params.customer, 'A customer is required to edit Onsite Recommendation settings.');
                return Utils.wrapDfd(this.websiteService.customerHasWebsite(this.customer.CustomerId()))
            }).then((customerHasWebsite) => {
              this.customerHasWebsite = customerHasWebsite;
            }).always(() => this.isLoading(false));
    }

    contentManagementSystemChanged(cms: Boo.Objects.ContentManagementSystem): void {
        this.updatedContentManagementSystem = cms;
    }

    save(saveType: SaveTypes): JQueryPromise<app.ticket.interfaces.ISaveData | void> {
        this.isLoading(true);

        return $.when(Utils.wrapDfd(this.publishOptionsComponent.save()))
            .then(() => $.when(...this.customerAccountGroups.toArray().map((x: CustomerAccountGroupComponent) => x.save(saveType))))
            .then(() => this.saveCustomerCMS())
            .then(() => super.save(saveType));
    }

    saveCustomerCMS(): JQueryPromise<any> {
        // Only save contentManagementSystem if it has changed
        if (this.updatedContentManagementSystem) {
            return Utils.wrapDfd(this.preAuditDetailService.updateContentManagementSystem(this.customer.CustomerId(), this.updatedContentManagementSystem.ContentManagementSystemId));
        }
        return System.emptyPromise();
    }

    validate(saveType: SaveTypes): JQueryPromise<IValidatedResult> {
        // validate the children components first, then return one result with all the messages
        let results: IValidatedResult[] = [];
        return $.when(...this.customerAccountGroups.toArray().flatMap((x: CustomerAccountGroupComponent) => x.validate(saveType)))
            .then((...accountResults: IValidatedResult[]) => {
                results = accountResults.concat(this.publishOptionsComponent.validate(saveType));

                if (this.currentImplementationOwner() === ImplementationOwners.Boostability
                    && this.cmsGroup
                    && !this.customerHasWebsite) {
                    const hasValidCredentials = this.cmsGroup.components.some(component => {
                        const account = ko.mapping.toJS(component.customerAccount);
                        if (account.CustomerAccountTypeId !== CustomerAccountTypes.CMSAdminAccess &&
                            account.CustomerAccountTypeId !== CustomerAccountTypes.FTP) {
                            return false;
                        }
                        const passwordIsSet = !!account.Password || !!account.SetPassword;
                        return !!account.Username && !!account.Url && passwordIsSet;
                    });
                    if (!hasValidCredentials) {
                        results.push({
                            isValid: false,
                            errorMessages: ['Login credentials for CMS or FTP are required if Boostability is making onsite edits for the customer.']
                        });
                    }
                }

                if (this.currentImplementationOwner() === ImplementationOwners.Boostability) {
                    if (!this.updatedContentManagementSystem) {
                        results.push({
                            isValid: false,
                            errorMessages: ['Customer CMS is required if Boostability is selected.']
                        })
                    }
                }
                
                return super.validate(saveType);
            })
            .then((ticketValidatedResult) => {
                ticketValidatedResult.isValid = ticketValidatedResult.isValid && results.every(x => x.isValid);
                ticketValidatedResult.errorMessages = ticketValidatedResult.errorMessages.concat(results.flatMap(x => x.errorMessages));

                if (!ticketValidatedResult.isValid) {
                    this.isOpen = true;
                }
                
                return ticketValidatedResult;
            });
    }
}